import { CopywritingCampaign } from "@/pages/Private/pages/List/pages/ListEditCopywriting/ListEditCopywriting";
import { getCampaignAiSnippetColumnIds } from "@/pages/Private/pages/List/components/CampaignOverview/components/Lead/utils/getAiSnippetStatus";

import { List } from "../../schema/list";

export interface PickerCampaign extends CopywritingCampaign {
	usesSnippets: boolean;
}

/**
 * The list's copywriting campaigns, shaped for the reach-out picker. `campaignConfig` is a map keyed
 * by campaign UUID plus a stray `name` metadata key, which is skipped. `usesSnippets` flags a
 * campaign whose sequence resolves AI-snippet columns, so the picker can offer the "Create AI
 * snippets" action.
 *
 * Invalid campaigns are **included** (PREC-3952): they used to be filtered out here, so a campaign
 * the user could see in the campaign table simply did not exist in the picker, with nothing said
 * about why. The picker renders them disabled with their reason; every caller that needs only
 * usable campaigns filters on `isValid` itself.
 */
export const deriveReachOutCampaigns = (list: List | undefined): PickerCampaign[] => {
	const config = (list?.campaignConfig ?? {}) as Record<
		string,
		CopywritingCampaign & { isValid?: boolean }
	>;

	return Object.entries(config)
		.filter(([key, campaign]) => key !== "name" && Boolean(campaign))
		.map(([id, campaign]) => ({
			...campaign,
			id,
			usesSnippets: getCampaignAiSnippetColumnIds(campaign.initialNodes, list?.columns).length > 0,
		}));
};

/**
 * Whether the list has at least one *valid* campaign whose sequence resolves an AI_SNIPPET column.
 * Gates the reach-out menu's "Run AI Snippets" bulk action — it only makes sense when a valid
 * campaign actually uses snippets.
 */
export const listHasValidCampaignUsingAiSnippets = (list: List | undefined): boolean =>
	deriveReachOutCampaigns(list).some((campaign) => campaign.isValid && campaign.usesSnippets);
